import React from 'react'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material'
import { TripTitleParts } from '@/src/utils/trips/tripListHelpers'

export type TripStatusTone = 'success' | 'error' | 'warning' | 'default'

export interface TripListCardProps {
    id: string
    tripId?: string
    tripType?: string
    titleParts: TripTitleParts
    subtitle?: string
    /** Shown under subtitle for LINKED (Cleartrip) bookings */
    bookedByLabel?: string
    /** MAIN = Flyin, LINKED = Cleartrip; used for redirect param ?account=LINKED */
    bookingSource?: 'MAIN' | 'LINKED'
    dateDay?: string
    dateMonth?: string
    dateLabel?: string
    statusLabel?: string
    statusTone: TripStatusTone
    onClick?: (tripId: string, bookingSource?: 'MAIN' | 'LINKED', tripType?: string) => void
}

const getStatusStyles = (tone: TripStatusTone) => {
    switch (tone) {
        case 'success':
            return { bgcolor: '#E8F5E9', color: '#2E7D32' }
        case 'error':
            return { bgcolor: '#FFEBEE', color: '#C62828' }
        case 'warning':
            return { bgcolor: '#FFF8E1', color: '#F57F17' }
        default:
            return { bgcolor: 'grey.100', color: 'text.secondary' }
    }
}

const statusBadgeSx = (statusStyles: Record<string, unknown>) => ({
    px: { xs: 1, md: 1.5 },
    py: { xs: 0.5, md: 1 },
    borderRadius: { xs: 0.75, md: 1 },
    fontSize: { xs: '0.6875rem', md: '0.75rem' },
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
    lineHeight: 1.4,
    textTransform: 'capitalize',
    ...statusStyles
})

/** Shared flight/trip card: date block, route, status badge, travellers, optional "Booked on Cleartrip" footer. Responsive. */
export const TripListCard: React.FC<TripListCardProps> = ({
    id,
    titleParts,
    subtitle,
    bookedByLabel,
    bookingSource,
    tripType,
    dateDay,
    dateMonth,
    dateLabel,
    statusLabel,
    statusTone,
    onClick
}) => {
    const statusStyles = getStatusStyles(statusTone)
    const showStatus = statusLabel && statusTone !== 'default'

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: { xs: 1.5, md: 2 },
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    borderColor: 'grey.300'
                }
            }}
        >
            <CardActionArea onClick={() => typeof onClick === 'function' && onClick(id, bookingSource, tripType)}>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    {/* Mobile (xs): status at top of card */}
                    {showStatus && (
                        <Box
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                px: { xs: 1.5, md: 2.5 },
                                pt: { xs: 1.5, md: 2.5 },
                                pb: 0.5
                            }}
                        >
                            <Typography component="span" sx={statusBadgeSx(statusStyles)}>
                                {statusLabel}
                            </Typography>
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            p: { xs: 1.5, md: 2.5 },
                            pt: { xs: showStatus ? 0.5 : 1.5, md: 2.5 }
                        }}
                    >
                        {(dateDay || dateLabel) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: { xs: 44, md: 56 },
                                    pr: { xs: 1.5, md: 2 },
                                    mr: { xs: 1.5, md: 2 },
                                    borderRight: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '16px', md: '18px' },
                                        lineHeight: 1.2,
                                        color: 'text.primary'
                                    }}
                                >
                                    {dateDay || '--'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: { xs: '10px', md: '12px' },
                                        color: 'text.secondary',
                                        textTransform: 'capitalize'
                                    }}
                                >
                                    {dateMonth || ''}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            {/* Title + subtitle in one column so spacing between them is fixed and not affected by status badge */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        lineHeight: 1.35,
                                        color: 'text.primary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    {titleParts.isRoundTrip && titleParts.origin && titleParts.destination ? (
                                        <>
                                            <Box component="span">{titleParts.origin}</Box>
                                            <SwapHorizIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            <Box component="span">{titleParts.destination}</Box>
                                        </>
                                    ) : (
                                        titleParts.fullTitle
                                    )}
                                </Typography>
                                {subtitle && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: { xs: '10px', md: '12px' },
                                            fontWeight: { xs: 500, md: 400 },
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>
                            {/* Desktop (md+): status on right, does not affect title-subtitle gap */}
                            {showStatus && (
                                <Typography
                                    component="span"
                                    sx={{
                                        ...statusBadgeSx(statusStyles),
                                        display: { xs: 'none', md: 'inline-block' },
                                        flexShrink: 0
                                    }}
                                >
                                    {statusLabel}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Booked on Cleartrip: full-width footer strip (reference design) */}
                    {bookedByLabel && (
                        <Box
                            sx={{
                                width: '100%',
                                px: { xs: 1.5, md: 2.5 },
                                py: 1,
                                background: 'linear-gradient(90deg, #F1F5F9 0%, #F9FAFB 100%)',
                                borderBottomLeftRadius: { xs: 1.5, md: 2 },
                                borderBottomRightRadius: { xs: 1.5, md: 2 }
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                }}
                            >
                                {bookedByLabel}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    )
}
